import { ART_PAL, fade, mix, n, shade, tint, vgrad, warm, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Mahjong Hand scene behind the game zone: a lacquered tea table seen from above (deep red-brown lacquer at night,
 * honey lacquer in light) under a warm lamp; the hand lies on a jade felt mat (the felt panel of the page). In the
 * corners, clear of the mat, the title row and the disclaimer: a teapot and a cup of tea top left, a bamboo sprig top
 * right, three mandarins on a saucer bottom left, a short wall of face-down tiles bottom right (backs only — no signs).
 * No grain or inlay lines run across the table (they would pass under the panels). The centre stays calm (the lamp
 * light only). No text, no filters. Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%).
 */
export function mahjongScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sm${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const lacquer = P.dark ? mix(P.bg, mix(P.red, P.orange, 0.35), 0.16) : mix(P.gold, '#FFFFFF', 0.8);
  const lacquerHi = P.dark ? mix(P.bg, mix(P.red, P.orange, 0.35), 0.22) : mix(P.gold, '#FFFFFF', 0.86);
  const lacquerLo = P.dark ? mix(P.bg, P.red, 0.09) : mix(P.orange, '#FFFFFF', 0.78);
  const gold = warm(P);
  const clay = P.dark ? mix(P.orange, P.bg, 0.55) : mix(P.orange, '#6B3A1E', 0.45);
  const celadon = P.dark ? mix(P.teal, P.bg, 0.55) : mix(P.teal, '#FFFFFF', 0.55);
  const tea = P.dark ? mix(P.gold, P.orange, 0.5) : mix(P.gold, P.orange, 0.3);
  const leaf = P.dark ? mix(P.green, P.bg, 0.5) : mix(P.green, P.teal, 0.3);
  const orange = P.dark ? mix(P.orange, P.bg, 0.15) : P.orange;
  const back = P.dark
    ? mix(mix(P.green, P.teal, 0.45), P.bg, 0.5)
    : mix(mix(P.green, P.teal, 0.4), P.slate, 0.2);
  const ivory = P.dark ? mix('#F5E9CF', P.bg, 0.15) : '#FBF3E0';
  const ink = shade(P.dark ? P.bg : P.orange, P.dark ? 0.4 : 0.55);

  const defs =
    `<defs>${vgrad(`${p}bg`, lacquerHi, lacquer, lacquerLo)}` +
    fade(`${p}lamp`, gold, P.dark ? 0.16 : 0.3) +
    fade(`${p}sh`, ink, P.dark ? 0.5 : 0.22) +
    `<radialGradient id="${p}cl" cx=".38" cy=".32" r=".75"><stop offset="0" stop-color="${tint(clay, 0.3)}"/><stop offset=".6" stop-color="${clay}"/><stop offset="1" stop-color="${shade(clay, 0.3)}"/></radialGradient>` +
    `<radialGradient id="${p}or" cx=".36" cy=".3" r=".8"><stop offset="0" stop-color="${tint(orange, 0.35)}"/><stop offset=".55" stop-color="${orange}"/><stop offset="1" stop-color="${shade(orange, 0.3)}"/></radialGradient>` +
    `</defs>`;

  const sh = (x: number, y: number, rx: number, ry: number) =>
    `<ellipse cx="${n(x + 8)}" cy="${n(y + 10)}" rx="${n(rx)}" ry="${n(ry)}" fill="url(#${p}sh)"/>`;
  /** teapot from above: body, lid, knob, spout, handle */
  const teapot = (x: number, y: number, r: number) =>
    `<g data-motif="${x}">${sh(x, y, r * 1.5, r * 1.4)}` +
    `<path d="M${n(x + r * 0.8)} ${n(y - r * 0.5)}L${n(x + r * 1.6)} ${n(y - r * 0.95)}L${n(x + r * 1.66)} ${n(y - r * 0.75)}L${n(x + r * 0.95)} ${n(y - r * 0.1)}Z" fill="${shade(clay, 0.1)}"/>` +
    `<path d="M${n(x - r * 0.75)} ${n(y + r * 0.6)}Q${n(x - r * 1.5)} ${n(y + r * 1.25)} ${n(x - r * 1.2)} ${n(y + r * 0.2)}" fill="none" stroke="${shade(clay, 0.1)}" stroke-width="${n(r * 0.16)}" stroke-linecap="round"/>` +
    `<circle cx="${x}" cy="${y}" r="${r}" fill="url(#${p}cl)"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(r * 0.42)}" fill="none" stroke="${shade(clay, 0.25)}" stroke-width="2"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(r * 0.12)}" fill="${tint(clay, 0.2)}"/></g>`;
  /** a cup of tea from above: celadon rim, amber tea, a small highlight */
  const cup = (x: number, y: number, r: number) =>
    `<g data-motif="${x}">${sh(x, y, r * 1.2, r * 1.15)}<circle cx="${x}" cy="${y}" r="${r}" fill="${celadon}"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(r * 0.78)}" fill="${tea}" fill-opacity="${o(0.85, 0.75)}"/>` +
    `<ellipse cx="${n(x - r * 0.3)}" cy="${n(y - r * 0.3)}" rx="${n(r * 0.22)}" ry="${n(r * 0.1)}" transform="rotate(-35 ${n(x - r * 0.3)} ${n(y - r * 0.3)})" fill="#FFFFFF" fill-opacity=".45"/></g>`;
  /** a bamboo sprig: a jointed stalk in from the edge and a few blade leaves */
  const bamboo = (x: number, y: number, s: number, flip: 1 | -1, rot: number) => {
    const X = (dx: number) => n(x + dx * s * flip);
    const Y = (dy: number) => n(y + dy * s);
    let leaves = '';
    for (const [dx, dy, a, l] of [
      [60, 0, -30, 70],
      [60, 0, 20, 60],
      [130, -4, -50, 64],
      [130, -4, 10, 72],
      [190, -8, -20, 56],
    ] as const) {
      const ang = ((flip === 1 ? a : 180 - a) * Math.PI) / 180;
      const lx = x + dx * s * flip;
      const ly = y + dy * s;
      const tx = lx + Math.cos(ang) * l * s;
      const ty = ly + Math.sin(ang) * l * s;
      const nx = -Math.sin(ang) * 9 * s;
      const ny = Math.cos(ang) * 9 * s;
      const mx = (lx + tx) / 2;
      const my = (ly + ty) / 2;
      leaves += `M${n(lx)} ${n(ly)}Q${n(mx + nx)} ${n(my + ny)} ${n(tx)} ${n(ty)}Q${n(mx - nx * 0.4)} ${n(my - ny * 0.4)} ${n(lx)} ${n(ly)}Z`;
    }
    return (
      `<g data-motif="${Math.round(x + 110 * s * flip)}" transform="rotate(${rot} ${x} ${y})" opacity="${o(0.9, 0.85)}">` +
      `<path d="M${X(-40)} ${Y(4)}L${X(210)} ${Y(-9)}" stroke="${leaf}" stroke-width="${n(8 * s)}" stroke-linecap="round"/>` +
      `<path d="M${X(60)} ${Y(-5)}V${Y(5)}M${X(130)} ${Y(-9)}V${Y(1)}" stroke="${shade(leaf, 0.3)}" stroke-width="${n(3 * s)}"/>` +
      `<path d="${leaves}" fill="${leaf}"/></g>`
    );
  };
  /** three mandarins on a round saucer (never four) */
  const mandarins = (x: number, y: number, r: number) => {
    let s = `<g data-motif="${x}">${sh(x, y, r * 2.6, r * 2.4)}<circle cx="${x}" cy="${y}" r="${n(r * 2.3)}" fill="${celadon}" fill-opacity="${o(0.8, 0.9)}"/>`;
    for (const [dx, dy] of [
      [-0.9, 0.5],
      [0.95, 0.4],
      [0, -0.85],
    ] as const) {
      const cx = x + dx * r;
      const cy = y + dy * r;
      s += `<circle cx="${n(cx)}" cy="${n(cy)}" r="${r}" fill="url(#${p}or)"/><path d="M${n(cx)} ${n(cy - r * 0.9)}q${n(r * 0.3)} ${n(-r * 0.35)} ${n(r * 0.6)} ${n(-r * 0.2)}" stroke="${leaf}" stroke-width="${n(r * 0.22)}" stroke-linecap="round"/>`;
    }
    return s + `</g>`;
  };
  /** a short wall of face-down tiles from above: two rows of five, the backs jade with an ivory rim */
  const wall = (x: number, y: number, tw: number, rot: number) => {
    const th = tw * 1.3;
    let s = '';
    for (let row = 0; row < 2; row++)
      for (let i = 0; i < 5; i++) {
        const tx = x + i * (tw + 2) + row * tw * 0.35;
        const ty = y + row * (th + 3);
        s +=
          `<rect x="${n(tx)}" y="${n(ty)}" width="${n(tw)}" height="${n(th)}" rx="${n(tw * 0.18)}" fill="${ivory}"/>` +
          `<rect x="${n(tx + 2)}" y="${n(ty + 2)}" width="${n(tw - 4)}" height="${n(th - 6)}" rx="${n(tw * 0.14)}" fill="${back}"/>`;
      }
    const cx = x + tw * 3;
    return `<g data-motif="${Math.round(cx)}" transform="rotate(${rot} ${n(cx)} ${n(y + th)})">${sh(cx - 8, y + th - 6, tw * 3.6, th * 1.4)}${s}</g>`;
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.48)}" rx="${n(W * 0.44)}" ry="${n(H * 0.46)}" fill="url(#${p}lamp)"/>`;

  // the wide scene is cropped to the game zone (cover): at 1440 × 900 about x 214…1386 is seen, the felt mat covers
  // y ≈ 320…590 across the whole zone, the title row the top, the disclaimer the bottom left
  if (wide)
    art +=
      teapot(300, 205, 50) +
      cup(420, 262, 26) +
      bamboo(1480, 170, 1, -1, 12) +
      mandarins(320, 720, 24) +
      wall(1180, 690, 30, -8);
  else art += bamboo(400, 60, 0.45, -1, 14) + cup(26, 700, 16);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Mahjong Hand: a row of tile edges, ivory with a jade back (repeat-x tile, 16 × 8). */
export function mahjongAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const back = P.dark ? mix(P.green, P.teal, 0.45) : mix(P.green, P.teal, 0.4);
  const ivory = P.dark ? '#E9DCC0' : '#F3E6C8';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<rect x="1.5" y="1" width="13" height="6.5" rx="1.5" fill="${ivory}" fill-opacity="${P.dark ? 0.75 : 1}"/>` +
    `<rect x="1.5" y="5" width="13" height="2.5" rx="1" fill="${back}" fill-opacity="${P.dark ? 0.85 : 0.9}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'mahjong', scene: mahjongScene, accent: mahjongAccent });
